import { type EventParticipant, isRegisteredEventParticipant } from 'models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Delete2LineIcon, Modal, message } from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { useGetUserNameWithFallback } from 'features/users/hooks';
import { useDeleteEventParticipants } from '../../queries';

export const useDeleteReminderInvitee = () => {
  const { t } = useTranslation();
  const [modal, contextHolder] = Modal.useModal();
  const { isPending: isLoadingDeletingRole, mutateAsync: deleteReminderInvitee } = useDeleteEventParticipants();
  const getUserNameWithFallback = useGetUserNameWithFallback();

  const confirm = useCallback(
    ({ eventId, invitee }: { eventId: string; invitee: EventParticipant }) => {
      const handleDeleteReminderInvitee = async () =>
        deleteReminderInvitee(
          {
            eventId,
            ...(isRegisteredEventParticipant(invitee)
              ? { participantRefs: [invitee.ref] }
              : { email: [invitee.email] }),
          },
          {
            onError() {
              message.error(
                t('Something went wrong. {{name}} couldn’t be removed from reminder', {
                  name: getUserNameWithFallback(invitee),
                }),
              );
            },
            onSuccess() {
              message.success(
                t('{{name}} removed from reminder successfully', {
                  name: getUserNameWithFallback(invitee),
                }),
              );
            },
          },
        );

      modal.confirm({
        content: 'You can add the user again at any time.',
        icon: <Delete2LineIcon type="error" />,
        okText: 'Remove',
        onOk: handleDeleteReminderInvitee,
        title: t('Are you sure you want to remove {{name}} from this reminder?', {
          name: getUserNameWithFallback(invitee),
        }),
        type: 'error',
      });
    },
    [deleteReminderInvitee, getUserNameWithFallback, modal, t],
  );

  return useMemo(
    () => ({ confirm, contextHolder, isLoadingDeletingRole }),
    [confirm, contextHolder, isLoadingDeletingRole],
  );
};
